import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useSessionStore } from '@/stores/session';
import { HelpOverlay } from '@/components/HelpOverlay/HelpOverlay';
import { AutosaveBanner } from '@/components/AutosaveBanner/AutosaveBanner';
import { helpVariant } from '@/lib/assetUi';
import styles from './AppShell.module.css';

/** Exposes the mobile-drawer close handler to descendants of the split body
 *  (mainly AssetNav) so picking an asset can collapse the drawer without
 *  threading callbacks through Generation. `null` outside split mode. */
const SidebarDrawerContext = createContext<{ close(): void } | null>(null);

export function useSidebarDrawer() {
  return useContext(SidebarDrawerContext);
}

interface Props {
  children: ReactNode;
  variant?: 'full' | 'logo-only';
  /** `scroll` (default) wraps children in a single vertical scroll pane —
   *  suitable for Welcome, Summary, ThankYou. `split` builds a two-pane
   *  layout (seit 2026-10-02): links die Sidebar über die volle Höhe mit dem
   *  Brand-Block (TRO-Logo, darunter der Markenname) oben, rechts der
   *  Content mit dem Header als Overlay — der Header gehört zum rechten
   *  Bereich und wird von der Timeline weggeschoben, das Logo bleibt. */
  contentMode?: 'scroll' | 'split';
  /** Required when `contentMode === 'split'`. Rendered as the left-hand
   *  scroll-isolated sidebar pane. */
  sidebar?: ReactNode;
  /** When true, plays the `troLogoGrow` entrance animation on the header
   *  logo (mirrors legacy/index.html lines 3399-3453, called from screen-0
   *  enter at lines 3033 / 3083 / 3361 / 3370). Welcome turns this on; all
   *  other screens leave the logo static. */
  animateLogo?: boolean;
}

/** Voice-talent chrome: slim header + full-width content. Admin pages use
 *  AdminLayout and are not affected. The `logo-only` variant is used for
 *  pre-config states (loading, password, error) where brand-name / help /
 *  mail / project-info cannot be resolved yet. */
export function AppShell({
  children,
  variant = 'full',
  contentMode = 'scroll',
  sidebar,
  animateLogo = false,
}: Props) {
  const config = useSessionStore((s) => s.config);
  const currentAssetIdx = useSessionStore((s) => s.currentAssetIdx);
  const customAssets = useSessionStore((s) => s.customAssets);
  const freeMode = useSessionStore((s) => s.freeMode);

  const brandName =
    variant === 'full' && config?.customBranding && config.brandName
      ? config.brandName
      : null;
  const contactEmail = variant === 'full' ? config?.contactEmail ?? null : null;
  const userName = useSessionStore((s) => s.name);
  const mailHref = useMemo(() => {
    if (!contactEmail) return null;
    const lang = config?.languageCode ? ` ${config.languageCode}` : '';
    const subject = encodeURIComponent(
      `Question ${userName ?? ''} — TRO Voice Studio${lang}`,
    );
    return `mailto:${contactEmail}?subject=${subject}`;
  }, [contactEmail, userName, config?.languageCode]);
  const projectInfo = useMemo(() => {
    if (variant !== 'full' || !config?.project) return null;
    const lang = config.languageCode ? ` — ${config.languageCode}` : '';
    return `${config.project}${lang}`;
  }, [variant, config?.project, config?.languageCode]);

  // Help-button visibility: hide on Welcome (`/:id` exactly) — there's no
  // active asset context yet, so "TTS Help" / "STS Help" would be
  // meaningless. Show on /generate + /upload + /done. Detect via path
  // suffix instead of exact-match so we don't have to know the tool ID.
  const { pathname } = useLocation();
  const showHelpButton = useMemo(() => {
    if (variant !== 'full') return false;
    return /\/(generate|upload|done)(\/|$)/.test(pathname);
  }, [variant, pathname]);

  // Aktives Asset — Basis für Help-Label und Guide-Variante. Seit Spec
  // 2026-09-21 § 6 braucht die Variante nicht nur den Typ, sondern auch das
  // `videoPath` des Assets, deshalb hier als eigenes Memo.
  const activeAsset = useMemo(() => {
    if (variant !== 'full' || !config) return undefined;
    return [...(config.assets ?? []), ...customAssets][currentAssetIdx];
  }, [variant, config, customAssets, currentAssetIdx]);

  // Determine active asset type (tts / sts / free) for help button labeling.
  // Free-Choice assets default to 'tts' when the user hasn't yet picked a
  // mode — mirrors FreeChoiceView's own default-mode rendering so the help
  // label matches what the user is actually looking at.
  const activeAssetType = useMemo(() => {
    if (!activeAsset) return null;
    if (activeAsset.assetType === 'free') {
      return (freeMode[currentAssetIdx] as 'tts' | 'sts' | undefined) ?? 'tts';
    }
    return activeAsset.assetType;
  }, [activeAsset, currentAssetIdx, freeMode]);

  const helpLabel =
    activeAssetType === 'sts'
      ? 'STS Help'
      : activeAssetType === 'tts'
        ? 'TTS Help'
        : 'Help';
  // Guide nach Asset-Typ und Video (Spec 2026-09-21 § 2): STS → STS-Guide,
  // TTS mit Video → voller TTS-Guide, TTS ohne Video → reduzierter
  // `simple`-Guide (ohne Video/Mixer/Background-Audio/Multi-Voice, dafür
  // mit Zeitlimit-Abschnitt). Für bestehende Multi-Projekte (nur TTS, kein
  // Video) bleibt es damit bei `simple` wie bisher.
  const helpGuideVariant = helpVariant(
    config,
    activeAsset,
    activeAssetType === 'sts' ? 'sts' : 'tts',
  );

  // Soft fade-in for brand-name + project-info pill. The `mounted` flag flips
  // on next tick so the initial render paints hidden, then the transition
  // kicks off. Re-runs when config changes so a session-restore shows the
  // same soft entrance.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    if (variant !== 'full') return;
    setMounted(false);
    const id = window.setTimeout(() => setMounted(true), 16);
    return () => window.clearTimeout(id);
  }, [variant, config?.project, config?.brandName]);

  // troLogoGrow entrance — mirrors legacy/index.html lines 3399-3453. Vanilla
  // toggles `.logo-small` off so a CSS `transition: width 0.7s
  // cubic-bezier(0.4,0,0.2,1)` (legacy line 84) expands the dot+text pill
  // from 126px to 95vw. The React header uses a single static `<img>` instead
  // of the dot+text pill, so we port the *entrance feel* (same 0.7s timing
  // and same easing) as a horizontal scale-in: the logo paints at scaleX(0)
  // on first frame, then transitions to scaleX(1). `prefers-reduced-motion`
  // skips the transform. Re-runs when Welcome remounts so navigating away
  // and coming back replays it (matches `troLogoReset` semantics — vanilla
  // re-adds `.logo-small` on goTo(0), and `troLogoGrow` removes it again).
  // Tab-session-scoped flag: once the logo entrance has played in this tab,
  // subsequent renders (PW gate → Welcome, Welcome → Generation → Welcome)
  // skip the fade so it doesn't re-animate every screen transition. Cleared
  // when the tab closes (sessionStorage scope), so a fresh visit replays it.
  const LOGO_ENTERED_KEY = 'tro_logo_entered';
  const [logoEntered, setLogoEntered] = useState(() => {
    try { return sessionStorage.getItem(LOGO_ENTERED_KEY) === '1'; } catch { return false; }
  });
  useEffect(() => {
    if (logoEntered) return;
    if (!animateLogo) {
      setLogoEntered(true);
      try { sessionStorage.setItem(LOGO_ENTERED_KEY, '1'); } catch { /* ignore */ }
      return;
    }
    // Two RAFs to mirror vanilla's `requestAnimationFrame(() =>
    // requestAnimationFrame(() => ...))` pattern at legacy line 3441 — paints
    // the small state first so the transition fires reliably.
    let raf2 = 0;
    const raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => {
        setLogoEntered(true);
        try { sessionStorage.setItem(LOGO_ENTERED_KEY, '1'); } catch { /* ignore */ }
      });
    });
    return () => {
      window.cancelAnimationFrame(raf1);
      if (raf2) window.cancelAnimationFrame(raf2);
    };
  }, [animateLogo, logoEntered]);

  const [helpOpen, setHelpOpen] = useState(false);

  // Markenname im Brand-Block der Sidebar: eine Zeile, kein Umbruch. Die
  // Schrift schrumpft von BRAND_FONT_MAX bis BRAND_FONT_MIN, bis der Text
  // in die Sidebar passt (Philip, 2026-10-02). Gemessen nach dem Layout, neu
  // bei jedem anderen Namen.
  const BRAND_FONT_MAX = 18;
  const BRAND_FONT_MIN = 10;
  const brandNameRef = useRef<HTMLSpanElement | null>(null);
  const [brandFontSize, setBrandFontSize] = useState(BRAND_FONT_MAX);
  useLayoutEffect(() => {
    const el = brandNameRef.current;
    if (!el || !brandName) return;
    let size = BRAND_FONT_MAX;
    el.style.fontSize = `${size}px`;
    while (size > BRAND_FONT_MIN && el.scrollWidth > el.clientWidth) {
      size -= 1;
      el.style.fontSize = `${size}px`;
    }
    setBrandFontSize(size);
  }, [brandName, contentMode]);

  // Mobile sidebar drawer (split mode + viewport < 900px). Closed by default;
  // opened via the header hamburger and closed by onSelect / backdrop / ESC.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const drawerCtx = useMemo(() => ({ close: closeDrawer }), [closeDrawer]);
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeDrawer();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen, closeDrawer]);
  // Auto-close when viewport widens past the breakpoint so the drawer state
  // doesn't get out of sync if the user resizes after opening it.
  useEffect(() => {
    if (contentMode !== 'split') return;
    const mq = window.matchMedia('(min-width: 900px)');
    const handler = (e: MediaQueryListEvent) => {
      if (e.matches) setDrawerOpen(false);
    };
    if (mq.matches) setDrawerOpen(false);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [contentMode]);

  // Header slide-away driven by the sticky Timeline. The content pane
  // scrolls 1:1 with wheel input. When the sticky Timeline's top edge
  // approaches the header's bottom (y=56), it pushes the header up
  // proportionally. Until then the header stays fully visible. We detect
  // the Timeline by its `data-sticky-push="header"` attribute so this
  // stays decoupled from TTSView / STSView internals.
  const rootRef = useRef<HTMLDivElement | null>(null);
  const contentScrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (contentMode !== 'split') return;
    const scroller = contentScrollRef.current;
    const root = rootRef.current;
    if (!scroller || !root) return;
    const HEADER_H = 56;
    // Small gap the pusher keeps to the header during the retraction so
    // the two edges never look like they touch.
    const PUSH_BUFFER = 6;
    let raf = 0;
    const tick = () => {
      raf = 0;
      const pusher = scroller.querySelector<HTMLElement>(
        '[data-sticky-push="header"]',
      );
      const top = pusher ? pusher.getBoundingClientRect().top : HEADER_H;
      const hide = Math.max(
        0,
        Math.min(HEADER_H, HEADER_H + PUSH_BUFFER - top),
      );
      root.style.setProperty('--header-hide', `${hide}px`);
    };
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(tick);
    };
    tick();
    scroller.addEventListener('scroll', onScroll, { passive: true });
    const ro = new ResizeObserver(onScroll);
    ro.observe(scroller);
    return () => {
      scroller.removeEventListener('scroll', onScroll);
      ro.disconnect();
      if (raf) window.cancelAnimationFrame(raf);
      root.style.removeProperty('--header-hide');
    };
  }, [contentMode]);

  // Scroll the content pane back to top whenever the active asset changes
  // (sidebar click, pill bar click, BottomBar Prev/Next). Without this the
  // new asset paints with the previous asset's scroll offset still applied,
  // which is jarring — especially when going from a long TTS asset to a
  // short STS one. `behavior: 'auto'` to avoid stacking a smooth scroll
  // animation on top of the already-busy re-render.
  useEffect(() => {
    if (contentMode !== 'split') return;
    const scroller = contentScrollRef.current;
    if (!scroller) return;
    scroller.scrollTo({ top: 0, behavior: 'auto' });
  }, [contentMode, currentAssetIdx]);

  const headerNode = (
    <header
      className={
        contentMode === 'split'
          ? `${styles.header} ${styles.headerOverlay}`
          : styles.header
      }
    >
      <div className={styles.left}>
        {contentMode === 'split' && sidebar && (
          <button
            type="button"
            className={styles.hamburger}
            onClick={() => setDrawerOpen((o) => !o)}
            aria-label={drawerOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={drawerOpen}
          >
            <i className={`ph-bold ${drawerOpen ? 'ph-x' : 'ph-list'}`} aria-hidden="true" />
          </button>
        )}
        {/* Im split-Modus trägt die Sidebar das Logo; hier erscheint es nur
            auf schmalen Viewports, wenn die Sidebar ein Drawer ist. */}
        <img
          src="/brand/tro-logo.png"
          alt="TRO"
          className={`${styles.logo} ${contentMode === 'split' ? styles.logoDrawerOnly : ''} ${animateLogo ? styles.logoGrow : ''} ${
            logoEntered ? styles.logoEntered : ''
          }`}
        />
        {brandName && contentMode !== 'split' && (
          <>
            <i
              className={`ph-bold ph-x ${styles.brandSep} ${mounted ? styles.entered : ''}`}
              aria-hidden="true"
            />
            <span
              className={`${styles.brandName} ${mounted ? styles.entered : ''}`}
            >
              {brandName}
            </span>
          </>
        )}
      </div>
      {variant === 'full' && (
        <div className={styles.right}>
          {projectInfo && (
            <span
              className={`${styles.projectInfo} ${mounted ? styles.entered : ''}`}
            >
              {projectInfo}
            </span>
          )}
          {showHelpButton && (
            <button
              type="button"
              className={styles.helpBtn}
              onClick={() => setHelpOpen(true)}
            >
              <i className="ph-bold ph-question-mark" aria-hidden="true" />
              {helpLabel}
            </button>
          )}
          {mailHref && (
            <a className={styles.mailBtn} href={mailHref}>
              <i className="ph-bold ph-envelope-simple" aria-hidden="true" />
              Questions? Mail us!
            </a>
          )}
        </div>
      )}
    </header>
  );

  // Brand-Block oben in der Sidebar (split-Modus): TRO-Logo, darunter der
  // Markenname bei Custom Branding — ohne Trenner, in einer Zeile.
  const brandBlock = (
    <div className={styles.brandBlock}>
      <img src="/brand/tro-logo.png" alt="TRO" className={styles.logo} />
      {brandName && (
        <span
          ref={brandNameRef}
          className={`${styles.sidebarBrandName} ${mounted ? styles.entered : ''}`}
          style={{ fontSize: `${brandFontSize}px` }}
        >
          {brandName}
        </span>
      )}
    </div>
  );

  return (
    <div className={styles.root} ref={rootRef}>
      {contentMode !== 'split' && headerNode}
      {/* Autosave-Fehler (Audit M8e) — nur im Talent-Chrome. Im scroll-Modus
          direkt unter dem Header; im split-Modus (Header ist Overlay) sitzt
          er sticky im Content-Pane, siehe unten. */}
      {variant === 'full' && contentMode !== 'split' && <AutosaveBanner />}
      {contentMode === 'split' ? (
        <SidebarDrawerContext.Provider value={drawerCtx}>
          <div className={styles.bodySplit}>
            {sidebar && (
              <div
                className={`${styles.sidebarSlot} ${drawerOpen ? styles.sidebarOpen : ''}`}
              >
                {brandBlock}
                <div className={styles.sidebarBody}>{sidebar}</div>
              </div>
            )}
            {drawerOpen && (
              <div
                className={styles.drawerBackdrop}
                onClick={closeDrawer}
                aria-hidden="true"
              />
            )}
            {/* Rechter Bereich: Header als Overlay NUR über dem Content —
                die Timeline schiebt ihn hoch, die Sidebar bleibt stehen. */}
            <div className={styles.contentPane}>
              {headerNode}
              <div className={styles.contentScroll} ref={contentScrollRef}>
                <div className={styles.headerSpacer} aria-hidden="true" />
                {variant === 'full' && (
                  <div className={styles.bannerSticky}>
                    <AutosaveBanner />
                  </div>
                )}
                {children}
              </div>
            </div>
          </div>
        </SidebarDrawerContext.Provider>
      ) : (
        <div className={styles.bodyScroll}>{children}</div>
      )}
      {variant === 'full' && (
        <HelpOverlay
          open={helpOpen}
          variant={helpGuideVariant}
          hideSfxAndCustom={config?._simpleMode === true}
          onClose={() => setHelpOpen(false)}
        />
      )}
    </div>
  );
}
